import type {PhotoCheckerPhotoEvaluationSaleVehiclePhoto} from '@dms/api/vehicle';

import {assignSafely} from 'shared/web';

import {photoCheckerPhotoTypesErrored} from './photoCheckerPhotoTypesByPart';

const PHOTO_CHECKER_PHOTO_TYPES_ERRORED = new Set<string>(photoCheckerPhotoTypesErrored);

/**
 * Extracts the remoteFileId (last path segment) from a storage URL.
 *
 * Album photos use .../resize/{remoteFileId}?token=...
 * PhotoChecker evals use .../get/{remoteFileId}?token=...
 *
 * Stripping to just the UUID makes both sides match regardless of the path prefix.
 */
function normalizePhotoUrl(url: string): string {
  try {
    const {pathname} = new URL(url);
    const lastSegment = pathname.split('/').filter(Boolean).at(-1);
    return lastSegment ?? url;
  } catch {
    const path = url.split('?')[0];
    const lastSegment = path.split('/').filter(Boolean).at(-1);
    return lastSegment ?? url;
  }
}

/**
 * Album badge score: `null` when the API classification uses a non-numeric badge (errored types).
 */
export function getPhotoCheckerEvalAlbumScore(
  evaluation: PhotoCheckerPhotoEvaluationSaleVehiclePhoto
): number | null {
  return PHOTO_CHECKER_PHOTO_TYPES_ERRORED.has(evaluation.photo_type) ? null : evaluation.score;
}

/**
 * Builds a lookup map from remoteFileId to the PhotoChecker eval row from the API.
 */
export function buildPhotoEvalMap(
  evals: PhotoCheckerPhotoEvaluationSaleVehiclePhoto[]
): Record<string, PhotoCheckerPhotoEvaluationSaleVehiclePhoto> {
  return evals.reduce<Record<string, PhotoCheckerPhotoEvaluationSaleVehiclePhoto>>((acc, item) => {
    assignSafely(acc, normalizePhotoUrl(item.url), item);
    return acc;
  }, {});
}

export function matchPhotoEval(
  imageUrl: string,
  evalMap: Record<string, PhotoCheckerPhotoEvaluationSaleVehiclePhoto>
): PhotoCheckerPhotoEvaluationSaleVehiclePhoto | undefined {
  return evalMap[normalizePhotoUrl(imageUrl)];
}
